import axios from 'axios';
import {BASE_URL} from '../constants/config';
import {toast} from 'react-hot-toast';
import {isMobile} from '../utils';
import useAuthStore from '../store/authStore';
const translate = str => str;

const API = axios.create({
  baseURL: BASE_URL,
  withCredentials: true
});

// Helper function to get CSRF token
function getCsrfToken() {
  // Option 1: From meta tag (if backend provides)
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) return metaTag.getAttribute('content');

  // Option 2: From cookie
  const cookies = document.cookie.split(';');
  const csrfCookie = cookies.find(c => c.trim().startsWith('XSRF-TOKEN='));
  if (csrfCookie) return csrfCookie.split('=')[1];

  // Option 3: From localStorage (if set during login)
  return localStorage.getItem('csrfToken');
}

// FOR ADDING HEADER TO EACH REQUESTS
API.interceptors.request.use(req => {
  req.headers['Content-Type'] = 'application/json';

  // Get token from Zustand store instead of parsing localStorage
  const token = useAuthStore.getState().token;
  const isMobileState = useAuthStore.getState().isMobile;
  const state = useAuthStore.getState();
  const isSuperAdminMasquerading = Boolean(state.isSuperAdminMasquerading) || Boolean(state.user?.is_impersonate) || Boolean(state.user?.isSuperAdminMasquerading);

  if (token) {
    req.headers['Authorization'] = token;
  }

  // Add CSRF token for state-changing operations
  const csrfToken = getCsrfToken();
  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(req.method)) {
    req.headers['X-CSRF-Token'] = csrfToken;
  }

  if (isMobileState || isMobile()) {
    req.headers['x-client-type'] = 'mobile';
  } else {
    req.headers['x-client-type'] = 'web';
  }

  if (isSuperAdminMasquerading && ['post', 'put', 'patch', 'delete'].includes(req.method?.toLowerCase() || req.method)) {
    // Allow report generation, exiting masquerade, and role switching
    if (req.url && (
      req.url.includes('/report') || 
      req.url.includes('/super-admin/exit-masquerade') || 
      req.url.includes('/super-admin/masquerade-role-switch') ||
      req.url.includes('/outside-books/fetch-urls') ||
      req.url.includes('/disciple-view') ||
      req.url.includes('/impersonate')
    )) {
      return req;
    }
    const error = new Error('Action ignored');
    error.isMasqueradeBlock = true;
    return Promise.reject(error);
  }

  return req;
});

API.interceptors.response.use(
  async res => {
    const suppressToast = res?.config?.headers?.['x-suppress-toast'] || res?.config?.headers?.['X-Suppress-Toast'];
    const isZipcodeEndpoint = String(res?.config?.url || '').includes('/zipcode');
    const isCampaignEndpoint = String(res?.config?.url || '').includes('/campaign') || String(res?.config?.url || '').includes('/user-campaign');
    const isMasqueradeIgnored = res?.data?.message === 'Action ignored in View-Only Mode.';
    if (isMasqueradeIgnored && !suppressToast) {
      toast.error(translate('View-Only Mode: Action ignored.'), {id: 'toast_message'});
    }
    if (!suppressToast && !isZipcodeEndpoint && !isCampaignEndpoint && !isMasqueradeIgnored && res?.data?.message !== 'success' && res?.request?.responseType !== 'blob') {
      toast.success(translate(res?.data?.message), {id: 'toast_message'});
    }
    return res;
  },
  async error => {
    if (error?.isMasqueradeBlock) {
      toast.error(translate('View-Only Mode: Action ignored.'), {id: 'toast_message'});
      return Promise.reject(error);
    }
    // Honour x-suppress-toast on errors too (callers that own their own error UX —
    // background refreshes, the public leader-invite page). Opt-in, so no other flow changes.
    const suppressToast = error?.config?.headers?.['x-suppress-toast'] || error?.config?.headers?.['X-Suppress-Toast'];
    const url = String(error?.config?.url || '');
    const method = String(error?.config?.method || '').toLowerCase();
    const isDiscipleReadingConcurrencyError = url.includes('/disciple-reading') && method === 'post' && error?.response?.status === 400 && error?.response?.data?.message === '';
    if (!suppressToast && !(String(error?.config?.url).startsWith('/zipcode') && error?.config?.method === 'get') && !isDiscipleReadingConcurrencyError) {
      toast.error(translate(error?.response?.data?.message) || translate('Something went wrong!'), {id: 'toast_message'});
    }
    if (!suppressToast && error.response?.status === 422) {
      const errors = error.response?.data;
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorValue = errors[firstErrorKey];
      toast.error(translate(String(firstErrorValue)), {id: 'toast_message'});
    }
    if (error.response?.status === 401) {
      const url = String(error?.config?.url || '');
      if (url.includes('/campaign') && error?.config?.method === 'get') {
        return Promise.reject(error);
      }
      toast.error(translate('Something went wrong!'), {id: 'toast_message'});
      localStorage.clear();
      window.location.reload();
    }
    if (error.response?.status === 500) {
      toast.error(translate('Something went wrong!'), {id: 'toast_message'});
      localStorage.clear();
      window.location.reload();
    }
    return error;
  }
);

// AUTH API'S
const loginUserApi = data => {
  return API.post('/login', data);
};
const checkInviteUser = inviteCode => {
  return API.get(`/signup/${inviteCode}`, {
    headers: {
      'x-suppress-toast': true
    }
  });
};
const registerInvitedUser = (inviteCode, data) => {
  return API.patch(`/signup/${inviteCode}`, data);
};
const registerNonInvitedUser = data => {
  return API.post(`/signup`, data);
};
const otpVerificationApi = data => {
  return API.post('/email-verification', data);
};
const resetPasswordSendOTPApi = email => {
  return API.get(`reset-password/${email}`);
};
const verifyAndResetPasswordApi = data => {
  return API.post('reset-password', data);
};
const changePasswordApi = data => {
  return API.post('change-password', data);
};

// USER API'S
const getUsersDetails = (userId, orgId) => {
  let url = `/users?userId=${userId}`;
  if (orgId) {
    url += `&orgId=${orgId}`;
  }
  return API.get(url);
};
const updateUsersDetails = (userId, data) => {
  return API.patch(`/users/${userId}`, data);
};
const inviteUser = data => {
  return API.post('/users', data);
};
const deleteUser = (usersid, role, org_id) => {
  return API.delete(`/users/${usersid}?role=${role}&org_id=${org_id}`);
};

// ASSESSMENT API'S
const getAssessmentsQuestions = (questions_ids, user_id, group_id) => {
  return API.get(`/questions?questions_ids=${questions_ids}&user_id=${user_id}&group_id=${group_id}`);
};
const getDiscipleQuestions = (questions_ids, user_id, group_id) => {
  return API.get(`/experience/${user_id}?questions_ids=${questions_ids}&group_id=${group_id}`);
};
const postAssessmentsQuestions = data => {
  return API.post('/assessment', data);
};
const getAssessmentAnswers = (user_id, question_ids) => {
  return API.get(`/assessment?question_ids=${question_ids}&user_id=${user_id}`);
};

// GROUP API'S
const getGroupApi = (id, user_id = '') => {
  return API.get(`/group?group_id=${id}&user_id=${user_id}`);
};
const updateGroupApi = (id, data, is_leader_update = '') => {
  return API.patch(`/group/${id}?is_leader_update=${is_leader_update}`, data);
};
const deleteGroupApi = (id, type = '') => {
  return API.delete(`/group/${id}?type=${type}`);
};
const archiveGroupApi = (id, data, is_leader_update = '') => {
  return API.patch(`/group/${id}?is_leader_update=${is_leader_update}`, data);
};
const getLeadersGroup = () => {
  return API.get('/leaders-groups');
};
const inviteDiscipleApi = data => {
  return API.post('/group-disciple', data);
};
const sendInviteToDisciple = data => {
  return API.post('/send-invite', data);
};
const getGroupDiscipleApi = (id, orgId = '') => {
  return API.get(`/group-disciple?group_id=${id}&orgId=${orgId}`);
};

// ORGANIZATIONS API'S
const addOrganization = data => {
  return API.post('/organization', data);
};
const deleteOrganization = id => {
  return API.delete(`/organization/${id}`);
};
const getOrganizationsApi = (id = '', is_public = false) => {
  return API.get(`/organization?user_id=${id}&is_public=${is_public}`);
};
const showOrganizationsApi = (id = '', name) => {
  return API.get(`/organization/${id}?name=${name}`);
};
const sendMeetingInvite = data => {
  return API.post('/group-meetings', data);
};
const updateMeetingApi = (id, data) => {
  return API.patch(`/group-meetings/${id}`, data);
};
const getMeetings = (id, user_id = '', requested_sequence = '', type = '', organization_id = '') => {
  return API.get(`/group-meetings?group_id=${id}&user_id=${user_id}&requested_sequence=${requested_sequence}&type=${type}&organization_id=${organization_id}`);
};
const getOutsideReadingPlanApi = id => {
  return API.get(`/group-outside-reading-plan/${id}`);
};
const updateOutsideReadingPlanApi = data => {
  return API.post('/group-outside-reading-plan', data);
};
const getBibleReadingPlanApi = id => {
  return API.get(`/group-bible-reading-plan/${id}`);
};
const updateBibleReadingPlanApi = data => {
  return API.post('/group-bible-reading-plan', data);
};
const postUpdateBibleReadingPlanApi = (id, data) => {
  return API.patch(`/group-bible-reading-plan/${id}`, data);
};
const updateOrganization = (id, data, name) => {
  return API.patch(`organization/${id}?name=${name}`, data);
};
const updatethemeOrganization = (id, theme_id, istheme) => {
  return API.patch(`organization/${id}`, {theme_id, istheme});
};
const updateOrganizationDefaultLanguage = (id, selectedLang) => {
  return API.patch(`organization/${id}`, {default_language: parseInt(selectedLang)});
};
const updateUserPreferedLangApi = (id, preferedLang) => {
  return API.patch(`user-prefered-lang/${id}`, {user_prefered_lang: parseInt(preferedLang)});
};

// LEADER API'S
const addExperienceApi = data => {
  return API.post('/experience', data, {headers: {'x-suppress-toast': 'true'}});
};
const getExperienceApi = ids => {
  return API.get(`/experience?questions_ids=${ids}`);
};
const addAddressApi = (data, config = {}) => {
  return API.post('/address', data, config);
};
const getAddressApi = id => {
  return API.get(id ? `/address?disciple_id=${id}` : '/address');
};
const updateAddressApi = data => {
  return API.patch('/address/123', data);
};
const getAddressByZipcode = zipcode => {
  return API.get(`/zipcode?zipcode=${zipcode}`, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};

const getReportApi = (data, viewType = 'generate') => {
  return API.post(`/report?view=${viewType}`, data);
};

// disciple apis
const deleteDiscipleApi = (id, isDiscipleDrop = false) => {
  return API.delete(`/group-disciple/${id}?isDiscipleDrop=${isDiscipleDrop}`);
};

// send email apis
const sendEmailApi = (data, type) => {
  return API.post(`/send-email?send_to=${type}`, data);
};

// reading plan apis
const discipleReadingApi = (requestedWeek, type, orgId = '') => {
  return API.get(`/disciple-reading/${requestedWeek}?type=${type}&organization_id=${orgId}`);
};

const downloadBibleReadingPlan = (type, groupId = '') => {
  return API.get(`/download-bible-reading-plan?type=${type}&group_id=${groupId}`, {
    responseType: 'blob'
  });
};
const markChapterReadApi = (data, type) => {
  return API.post(`/disciple-reading?type=${type}`, data);
};

const getBadges = (group_disciples_id, group_id) => {
  return API.get(`/badges/${group_disciples_id}?gp_id=${group_id}`);
};

const addDisciple = data => {
  return API.post('/group-disciple', data);
};
const checkDiscipleEmailApi = (email, orgId) => {
  return API.get(`/group-disciple/check-email?email=${encodeURIComponent(email)}&organization_id=${orgId}`);
};
const updateDiscipleApi = (group_disciple_id, data, config = {}) => {
  return API.patch(`/group-disciple/${group_disciple_id}`, data, config);
};

const activateDisciple = data => {
  return API.post('/activate-disciple', data);
};

// update user role apis
const addUserRoleApi = (data, config = {}) => {
  return API.post(`/user-role`, data, config);
};
const removeUserRoleApi = (id, permission, orgId) => {
  return API.patch(`/user-role/${id}`, {
    permission,
    orgId
  });
};

// admin dashboard api
const getAdminData = async orgId => {
  return API.get(`admin?organization_id=${orgId}`);
};

const getDashboardData = async (org_id = '', filter = '') => {
  return API.get(`dashboard?org_id=${org_id}&filter=${filter}`);
};

const getDashboardPhaseData = (org_id = '', filter = '') => API.get(`dashboard?org_id=${org_id}&filter=${filter}&name=phase`);

const getDashboardQuickLookData = (org_id = '') => API.get(`dashboard?org_id=${org_id}&name=quicklook`);

const getDashboardProgressionData = (org_id = '') => API.get(`dashboard?org_id=${org_id}&name=progression`);

const getDashboardYearStats = (org_id = '', filter = '') => API.get(`dashboard?org_id=${org_id}&filter=${filter}&name=yearstats`);

const updateUserRole = async (userId, data) => {
  return API.patch(`update-user-role/${userId}`, data);
};

const getDiscipleDetailsApi = async (user_id, name, orgId = '') => {
  return API.get(`admin-disciple?user_id=${user_id}&name=${name}${orgId ? `&organization_id=${orgId}` : ''}`);
};

const getLeaderDiscipleDetailsApi = async (user_id, name, orgId) => {
  return API.get(`admin-disciple/${user_id}?name=${name}&organization_id=${orgId}`);
};

const getAdminLeadersGroupApi = (id, details = '', organization = '') => {
  return API.get(`/leaders-groups/${id}?isShow=${details}&organization=${organization}`);
};

const getAdminPromoteDiscipleApi = (org_id = '') => {
  return API.get(`/promote-invite-leader/?org_id=${org_id}`);
};
const addLeaderApi = data => {
  return API.post('/promote-invite-leader', data);
};
const getAdminOrgDisciplesApi = (orgId, name, isNominated = false, isSideBarCount = false) => {
  return API.get(`/group-disciple/${orgId}?name=${name}&is_nominated=${isNominated}&is_side_bar_count=${isSideBarCount}`);
};

const getAllDisciples = () => {
  return API.get(`/group-disciple`);
};
const getAdminGroupsApi = (orgId, name = '') => {
  return API.get(`/group/${orgId}?name=${name}`);
};
const markAttendenceOfGroupMeeting = data => {
  return API.post(`/meeting-attendance`, data);
};
const getAttendenceOfGroupMeeting = (meeting_id, discipleId = '') => {
  return API.get(`/meeting-attendance/${meeting_id}?disicpleId=${discipleId}`);
};
const getAllReadingPlansApi = (orgId = '', name = '') => {
  return API.get(`/reading-plans?orgId=${orgId}&name=${name}`);
};
const updateReadingPlansApi = (id, data, isCheck = '', name = '') => {
  return API.patch(`/reading-plans/${id}?is_check=${isCheck}&name=${name}`, data);
};

const getAllAvailableBooks = (org_id = '', year = '') => {
  return API.get(`/outside-books?orgId=${org_id}&year=${year}`);
};

const getAllReadingPlansBooks = (id, groupId = '') => {
  return API.get(`/reading-plan-books/${id}?groupId=${groupId}`);
};
const addPlanApi = (data, is_duplicate = '') => {
  return API.post(`/reading-plans?is_duplicate=${is_duplicate}`, data);
};
const deletePlanApi = id => {
  return API.delete(`/reading-plans/${id}`);
};

const addBookApi = data => {
  return API.post(`/outside-books`, data);
};

const updateBookApi = (id, data) => {
  return API.patch(`/outside-books/${id}`, data);
};

const deleteBookApi = (id, data) => {
  return API.delete(`/outside-books/${id}`);
};
const getAllPublishedBooks = (org_id = '', groupId = '') => {
  return API.get(`/organization-published-outside-books?orgId=${org_id}&groupId=${groupId}`);
};
const addBookInReadingPlanHandler = data => {
  return API.post(`/reading-plan-books`, data);
};
const updatePublishedBooksApi = (id, data) => {
  return API.patch(`/organization-published-outside-books/${id}`, data);
};
const promoteUserHandlerApi = (id, data) => {
  return API.patch(`/leader-level/${id}`, data);
};
const getLeaderOutsideReadingApi = (orgId = '', groupId = '') => {
  return API.get(`/leader-outside-reading?orgId=${orgId}&groupId=${groupId}`);
};
const updateLeaderOutsideReadingApi = (groupId, planId, data) => {
  return API.post(`/leader-outside-reading?groupId=${groupId}&planId=${planId}`, data);
};
const getAllUsersApi = orgId => {
  return API.get(`/all-user?organization=${orgId}`);
};
const getUnassignedUsersApi = () => {
  return API.get(`/unassigned-user`);
};
const getUserDetailApi = (userId, orgId) => {
  return API.get(`/all-user?user_id=${userId}&organization=${orgId}`);
};
const impersonateDiscipleApi = (userId = '') => {
  return API.get(`/disciple-view?userId=${userId}`);
};
const updateOrganizationConfigApi = (id, data) => {
  return API.patch(`/update-organization-configuration/${id}`, data);
};
const createCampaignApi = data => {
  return API.post('/campaign', data);
};
const getCampaignsApi = () => {
  return API.get(`/campaign`, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};
const getCampaignDetailsApi = campaignId => {
  return API.get(`/campaign/${campaignId}`);
};
const updateCampaignApi = (campaignId, data) => {
  return API.patch(`/campaign/${campaignId}`, data);
};
const deleteCampaignApi = campaignId => {
  return API.delete(`/campaign/${campaignId}`);
};

const markCampaignAsShownApi = (campaignId) => {
  return API.post('/user-campaign/mark-shown', { campaignId }, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};

const markCampaignAsDismissedApi = (campaignId) => {
  return API.post('/user-campaign/mark-dismissed', { campaignId }, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};

const markCampaignAsConfirmedApi = (campaignId) => {
  return API.post('/user-campaign/mark-confirmed', { campaignId }, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};

const getUserCampaignStatusApi = (campaignId) => {
  return API.get(`/user-campaign/status/${campaignId}`, {
    headers: {
      'x-suppress-toast': 'true',
      'X-Suppress-Toast': 'true'
    }
  });
};

const getAllUserCampaignsApi = () => {
  return API.get('/user-campaign/all');
};

const updatepasswordApi = (passwordId, data) => {
  return API.patch(`/password/${passwordId}`, data);
};

// Outside books API
const fetchBookUrlsApi = books => {
  return API.post('/outside-books/fetch-urls', {books});
};

const superAdminMasqueradeApi = data => {
  return API.post('/super-admin/masquerade', data);
};

const superAdminExitMasqueradeApi = () => {
  return API.post('/super-admin/exit-masquerade');
};

const superAdminMasqueradeRoleSwitchApi = data => {
  return API.post('/super-admin/masquerade-role-switch', data);
};

const getUserDependenciesApi = (userId, currentOrgId) => {
  return API.get(`/super-admin/user-dependencies?userId=${userId}&currentOrgId=${currentOrgId || ''}`);
};

const transferUserOrganizationApi = data => {
  return API.post('/super-admin/transfer-user-org', data);
};

// Draft Day / Planner (SIAA-579) API. All calls are org-scoped; the backend
// verifies caller-org membership (F-028 guard) on every endpoint.
const getLaunchesApi = orgId => {
  // Background list load — suppress the success toast so it doesn't flash on every visit.
  return API.get(`/launches?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const getLaunchApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}?org=${orgId}`);
};
const createLaunchApi = data => {
  return API.post('/launches', data);
};
const updateLaunchApi = (launchId, data) => {
  return API.patch(`/launches/${launchId}`, data);
};
const deleteLaunchApi = (launchId, orgId) => {
  return API.delete(`/launches/${launchId}?org=${orgId}`);
};
// Draft Day step progress (numbered stepper).
const getLaunchProgressApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/progress?org=${orgId}`);
};
// Open Signup settings (SIAA-580).
const getOpenSignupApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/open-signup?org=${orgId}`);
};
const saveOpenSignupApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/open-signup`, data);
};
// Per-org Draft Day settings (F-065) — org-wide defaults applied to every Draft Day the org
// runs, as opposed to the per-launch open_signup above. Built with the backend but had no
// frontend until SIAA-636 needed the org's location palette. The GET is a background load
// alongside the Open Signup fetch, so suppress its toast.
// Set a pool disciple's gender from the Draft Day Disciples screen (SIAA-643). Writes the USER
// record — the AI grouping that consumes it runs before launch, so staging it would be too late.
const setDiscipleGenderApi = (userId, data) => {
  return API.post(`/draft-disciples/${userId}/gender`, data);
};
const getDraftDaySettingsApi = orgId => {
  return API.get(`/draft-day-settings?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const saveDraftDaySettingsApi = data => {
  return API.post('/draft-day-settings', data);
};
// Auto-Promote (SIAA-589) — list promotable groups + save staged promotions.
// The GET is a background refresh; suppress its success toast so it doesn't read
// as "success" after a failed action.
const getPromotableGroupsApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/promotable?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const autoPromoteApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/auto-promote`, data);
};
const forceFinishApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/force-finish`, data);
};
const getCarryoverApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/carryover?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
// Invite Leaders + Pool (SIAA-583/584). The leaders list is a background refresh
// (suppress its success toast); the pool toggle is a deliberate user action (keep
// its toast). Inviting/resending a leader reuses the live inviteUser (POST /users).
const getLaunchLeadersApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/leaders?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const setLeaderPoolApi = (launchId, data) => {
  return API.put(`/launches/${launchId}/leader-pool`, data);
};

// Turn a leader's recommendation into the approval the board enforces.
// data: { org, user_id, level } — `level` is the HIGHEST year; the years below it are
// granted with it. Send level 'NA' to remove the approval.
const approveToLeadApi = (launchId, data) => {
  return API.put(`/launches/${launchId}/approve-to-lead`, data);
};
// Invite (bulk user_ids and/or a new {name,email}), resend, and undo — all org-scoped.
const inviteLeadersApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/leader-invites`, data);
};
const resendLeaderInvitesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/leader-invites/resend`, data);
};
const revertLeaderInvitesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/leader-invites/undo`, data);
};
// PUBLIC (token is the credential) — leader-facing accept/decline page. The page owns
// its own error UX (friendly "invitation unavailable"), so suppress the global toast.
const getLeaderInviteApi = token => {
  return API.get(`/draft-leader-invites/${token}`, {headers: {'x-suppress-toast': '1'}});
};
const respondLeaderInviteApi = (token, action, data) => {
  return API.post(`/draft-leader-invites/${token}/${action}`, data || {}, {headers: {'x-suppress-toast': '1'}});
};
// Invite Disciples + Accept (SIAA-586/587). The list load is a background refresh
// (suppress its toast); invite/import/resend/undo are deliberate actions (keep toasts).
const getLaunchDisciplesApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/disciples?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const importDisciplesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/disciple-import`, data);
};
const inviteDisciplesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/disciple-invites`, data);
};
const resendDiscipleInvitesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/disciple-invites/resend`, data);
};
const revertDiscipleInvitesApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/disciple-invites/undo`, data);
};
// PUBLIC — 587 disciple accept/decline page (own error UX, so suppress the global toast).
const getDiscipleInviteApi = token => {
  return API.get(`/draft-disciple-invites/${token}`, {headers: {'x-suppress-toast': '1'}});
};
const respondDiscipleInviteApi = (token, action, data) => {
  return API.post(`/draft-disciple-invites/${token}/${action}`, data || {}, {headers: {'x-suppress-toast': '1'}});
};
// PUBLIC — self-serve join link / QR (SIAA-586). Own error UX → suppress global toast.
const getDraftJoinApi = launchId => {
  return API.get(`/draft-join/${launchId}`, {headers: {'x-suppress-toast': '1'}});
};
const submitDraftJoinApi = (launchId, data) => {
  return API.post(`/draft-join/${launchId}`, data || {}, {headers: {'x-suppress-toast': '1'}});
};
// Review & Adjust Groups / the board (SIAA-591). The board GET is a background refresh
// (suppress its toast); the mutating actions are deliberate (keep toasts). Submit/validate
// surface their own error lists, so suppress the generic toast on those.
// `includeAll` widens the draftable pool past accepted-only — for drafting people who cannot
// accept an invite themselves (no email). Default is unchanged.
const getBoardApi = (launchId, orgId, includeAll = false) => {
  const qs = `org=${orgId}${includeAll ? '&include=all' : ''}`;
  return API.get(`/launches/${launchId}/board?${qs}`, {headers: {'x-suppress-toast': '1'}});
};
const placeDiscipleApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/place`, data);
};
const boardAssignLeaderApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/leader`, data);
};
const boardLockApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/lock`, data);
};
const boardSaveGroupApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/group`, data);
};
const boardDeleteGroupApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/group/delete`, data);
};
const boardValidateApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/board/validate?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const boardSubmitApi = (launchId, data) => {
  return API.post(`/launches/${launchId}/board/submit`, data, {headers: {'x-suppress-toast': '1'}});
};
// Draft Day participant notifications (SIAA-593). Both endpoints work on a LAUNCHED season —
// the usual editable-launch guard would reject exactly the state these are for.
const launchNotifyStatusApi = (launchId, orgId) => {
  return API.get(`/launches/${launchId}/notify?org=${orgId}`, {headers: {'x-suppress-toast': '1'}});
};
const launchNotifySendApi = (launchId, orgId, data) => {
  return API.post(`/launches/${launchId}/notify?org=${orgId}`, data || {}, {headers: {'x-suppress-toast': '1'}});
};
// Super Admin ▸ Server Logs. Superadmin-only server side; the log spans every organisation.
const notificationLogsApi = (params) => {
  const qs = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return API.get(`/notification-logs${qs ? `?${qs}` : ''}`, {headers: {'x-suppress-toast': '1'}});
};
const notificationLogFacetsApi = () => {
  return API.get('/notification-logs/facets', {headers: {'x-suppress-toast': '1'}});
};
const notificationLogRetryApi = (id) => {
  return API.post(`/notification-logs/${id}/retry`, {}, {headers: {'x-suppress-toast': '1'}});
};
// AI Grouping Assistant (SIAA-588) — deterministic suggest + commit locked groups.
const aiSuggestApi = (launchId, orgId, params, excludeUserIds) => {
  return API.post(`/launches/${launchId}/ai-suggest`, {org: orgId, params, exclude_user_ids: excludeUserIds || []}, {headers: {'x-suppress-toast': '1'}});
};
// `params` carries the wizard's size window so committed groups get the target_min/max the
// solver actually used (not a hardcoded 4–10).
const aiCommitApi = (launchId, orgId, groups, params) => {
  return API.post(`/launches/${launchId}/ai-commit`, {org: orgId, groups, params}, {headers: {'x-suppress-toast': '1'}});
};

export {
  getLaunchesApi,
  getLaunchApi,
  createLaunchApi,
  updateLaunchApi,
  deleteLaunchApi,
  getLaunchProgressApi,
  getOpenSignupApi,
  saveOpenSignupApi,
  getDraftDaySettingsApi,
  saveDraftDaySettingsApi,
  setDiscipleGenderApi,
  getPromotableGroupsApi,
  autoPromoteApi,
  forceFinishApi,
  getCarryoverApi,
  getLaunchLeadersApi,
  setLeaderPoolApi,
  approveToLeadApi,
  inviteLeadersApi,
  resendLeaderInvitesApi,
  revertLeaderInvitesApi,
  getLeaderInviteApi,
  respondLeaderInviteApi,
  getLaunchDisciplesApi,
  importDisciplesApi,
  inviteDisciplesApi,
  resendDiscipleInvitesApi,
  revertDiscipleInvitesApi,
  getDiscipleInviteApi,
  respondDiscipleInviteApi,
  getDraftJoinApi,
  submitDraftJoinApi,
  getBoardApi,
  placeDiscipleApi,
  boardAssignLeaderApi,
  boardLockApi,
  boardSaveGroupApi,
  aiSuggestApi,
  aiCommitApi,
  boardDeleteGroupApi,
  boardValidateApi,
  boardSubmitApi,
  launchNotifyStatusApi,
  launchNotifySendApi,
  notificationLogsApi,
  notificationLogFacetsApi,
  notificationLogRetryApi,
  impersonateDiscipleApi,
  sendEmailApi,
  loginUserApi,
  checkInviteUser,
  registerInvitedUser,
  registerNonInvitedUser,
  otpVerificationApi,
  resetPasswordSendOTPApi,
  verifyAndResetPasswordApi,
  getUsersDetails,
  getAssessmentsQuestions,
  postAssessmentsQuestions,
  getGroupApi,
  updateGroupApi,
  deleteGroupApi,
  inviteDiscipleApi,
  sendInviteToDisciple,
  getLeadersGroup,
  inviteUser,
  addOrganization,
  getOrganizationsApi,
  updateOrganization,
  sendMeetingInvite,
  addAddressApi,
  addExperienceApi,
  getExperienceApi,
  getAddressApi,
  updateAddressApi,
  getAddressByZipcode,
  getReportApi,
  deleteDiscipleApi,
  updateMeetingApi,
  getMeetings,
  getOutsideReadingPlanApi,
  updateOutsideReadingPlanApi,
  discipleReadingApi,
  deleteUser,
  getBibleReadingPlanApi,
  updateBibleReadingPlanApi,
  downloadBibleReadingPlan,
  addUserRoleApi,
  removeUserRoleApi,
  markChapterReadApi,
  postUpdateBibleReadingPlanApi,
  getAdminData,
  updateUsersDetails,
  updateUserRole,
  getAdminOrgDisciplesApi,
  getDiscipleDetailsApi,
  getAdminLeadersGroupApi,
  getGroupDiscipleApi,
  getLeaderDiscipleDetailsApi,
  getAdminGroupsApi,
  getDashboardData,
  getDashboardPhaseData,
  getDashboardQuickLookData,
  getDashboardProgressionData,
  getDashboardYearStats,
  getDiscipleQuestions,
  markAttendenceOfGroupMeeting,
  getAttendenceOfGroupMeeting,
  getBadges,
  changePasswordApi,
  updatethemeOrganization,
  getAllDisciples,
  getAllReadingPlansApi,
  updateOrganizationDefaultLanguage,
  addDisciple,
  checkDiscipleEmailApi,
  activateDisciple,
  getAllAvailableBooks,
  getAllPublishedBooks,
  addBookApi,
  updateBookApi,
  deleteBookApi,
  updatePublishedBooksApi,
  updateReadingPlansApi,
  getAllReadingPlansBooks,
  deletePlanApi,
  addPlanApi,
  addBookInReadingPlanHandler,
  promoteUserHandlerApi,
  updateDiscipleApi,
  getLeaderOutsideReadingApi,
  updateLeaderOutsideReadingApi,
  showOrganizationsApi,
  getAdminPromoteDiscipleApi,
  addLeaderApi,
  archiveGroupApi,
  updateUserPreferedLangApi,
  deleteOrganization,
  getAssessmentAnswers,
  getAllUsersApi,
  getUnassignedUsersApi,
  getUserDetailApi,
  updateOrganizationConfigApi,
  createCampaignApi,
  getCampaignsApi,
  getCampaignDetailsApi,
  updateCampaignApi,
  deleteCampaignApi,
  markCampaignAsShownApi,
  markCampaignAsDismissedApi,
  markCampaignAsConfirmedApi,
  getUserCampaignStatusApi,
  getAllUserCampaignsApi,
  updatepasswordApi,
  fetchBookUrlsApi,
  superAdminMasqueradeApi,
  superAdminExitMasqueradeApi,
  superAdminMasqueradeRoleSwitchApi,
  getUserDependenciesApi,
  transferUserOrganizationApi
};
